import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { testimonialsData } from '@/mocks/home';
import { getGenericWhatsAppUrl } from '@/utils/whatsapp';
import { useScrollAnimation, useCountUp } from '@/hooks/useScrollAnimation';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

const avatarStyles = [
  { bg: 'bg-primary-50', icon: 'text-primary-500' },
  { bg: 'bg-accent-50', icon: 'text-accent-500' },
  { bg: 'bg-secondary-50', icon: 'text-secondary-500' },
];
const getAvatarStyle = (id: number) => avatarStyles[(id - 1) % 3];

function StatCounter({ value, label, isVisible }: { value: number; label: string; isVisible: boolean }) {
  const count = useCountUp(value, 2000, isVisible);
  const displayValue = value >= 100 ? `${count}+` : value === 5 ? '5.0' : `${count}%`;
  return (
    <div className="text-center">
      <div className="font-heading font-bold text-3xl md:text-4xl text-white mb-2">{displayValue}</div>
      <div className="text-sm text-white/80">{label}</div>
    </div>
  );
}

export default function TestimonialsPage() {
  const { ref: gridRef, isVisible } = useScrollAnimation(0.03);
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation(0.08);

  return (
    <Layout>
      <JsonLd data={getWebPageSchema('Client Testimonials & Reviews | Infinite Consultant & Services Bangalore', 'Read genuine client reviews and testimonials for Infinite Consultant & Services — 500+ satisfied customers across Bangalore for landscaping interiors construction and property maintenance.', 'https://infiniteconsultant.com/testimonials')} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }, { name: 'Testimonials', url: 'https://infiniteconsultant.com/testimonials' }])} />
      {/* Hero */}
      <section className="relative h-[350px] md:h-[450px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Happy%20Indian%20families%20and%20professionals%20in%20modern%20beautiful%20Bangalore%20homes%20and%20offices%20smiling%20satisfied%20Indian%20customers%20lifestyle%20photography%20warm%20natural%20light%20professional&width=1600&height=800&seq=testimonials-hero&orientation=landscape&nocache=true"
          alt="Client Testimonials"
          className="absolute inset-0 w-full h-full object-cover object-top animate-reveal"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 sm:px-6 lg:px-10 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
                <span className="text-white/50">/</span>
                <span className="text-white whitespace-nowrap">Testimonials</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-3" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards' }}>
                What Our Clients Say
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards' }}>
                Real feedback from real clients who trusted us with their property transformation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div ref={gridRef} className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {testimonialsData.map((t, i) => (
                <div
                  key={t.id}
                  className={`bg-white rounded-xl p-6 md:p-8 border border-background-200/70 hover:shadow-sm transition-all duration-500 flex flex-col ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  {t.projectThumbnail && (
                    <div className="mb-4 rounded-lg overflow-hidden h-32">
                      <img
                        src={t.projectThumbnail}
                        alt={`${t.name}'s project`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-0.5 mb-4">
                    {[...Array(t.rating)].map((_, idx) => (
                      <div key={idx} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm" />
                      </div>
                    ))}
                  </div>
                  <blockquote className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 flex-1 italic">
                    &ldquo;{t.text}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-3 pt-4 border-t border-background-100">
                    <div className={`w-12 h-12 rounded-full shrink-0 flex items-center justify-center ${getAvatarStyle(t.id).bg}`}>
                      <i className={`ri-user-line text-xl ${getAvatarStyle(t.id).icon}`} />
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-sm text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section ref={statsRef} className="py-14 md:py-20 bg-gradient-brand">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto text-center">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-10">
              <StatCounter value={500} label="Happy Clients" isVisible={statsVisible} />
              <StatCounter value={5} label="Average Rating" isVisible={statsVisible} />
              <StatCounter value={100} label="Would Recommend" isVisible={statsVisible} />
              <StatCounter value={10} label="Years of Service" isVisible={statsVisible} />
            </div>
            <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto mb-8">
              Join 500+ satisfied clients who have transformed their properties with Infinite Consultant & Services.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="btn-ripple w-full sm:w-auto px-8 py-3.5 rounded-lg bg-white text-primary-600 font-semibold text-sm hover:bg-background-50 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                Start Your Project
              </Link>
              <a
                href={getGenericWhatsAppUrl()}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-white/10 text-white border border-white/20 font-medium text-sm hover:bg-white/20 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2" />
                Enquire Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}